import { useEffect, useState } from 'react'
import debounce from 'lodash/debounce'
import { Modal, Paper } from '@mui/material'
import { Draggable, type DraggableBounds } from '~/shared/ui'
import { SessionHelp } from './SessionHelp'

const MULTIPLIER = 0.8
const getHeight = () => Math.round(window.innerHeight * MULTIPLIER)
const getWidth = () => Math.round(getHeight() * MULTIPLIER)
const getBounds = (modalHeight: number, modalWidth: number): DraggableBounds => ({
  left: -((window.innerWidth - modalWidth) / 2),
  top: -((window.innerHeight - modalHeight) / 2),
  right: (window.innerWidth - modalWidth) / 2,
  bottom: (window.innerHeight - modalHeight) / 2,
})

export const SessionHelpModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [height, setHeight] = useState<number>(getHeight())
  const [width, setWidth] = useState<number>(getWidth())

  // Change modal bounds when window size changes. The debounced handler is created inside the
  // effect, which is where its lifetime actually is: it used to be rebuilt on every render
  // even though only the first one was ever registered.
  useEffect(() => {
    const handleResize = debounce(
      () => {
        setHeight(getHeight())
        setWidth(getWidth())
      },
      250,
      { trailing: true },
    )
    window.addEventListener('resize', handleResize)

    return () => {
      handleResize.cancel()
      window.removeEventListener('resize', handleResize)
    }
  }, [])
  const bounds = getBounds(height, width)

  const handleClose = () => {
    onClose()
  }

  return (
    <Modal
      disableAutoFocus
      disableEnforceFocus
      hideBackdrop
      open
      onClose={handleClose}
      sx={{ pointerEvents: 'none' }}
    >
      <div>
        <Draggable
          bounds={bounds}
          positionOffset={{
            x: '-50%',
            y: '-50%',
          }}
        >
          <Paper
            elevation={10}
            sx={{
              height,
              width,
              pointerEvents: 'all',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              position: 'absolute',
              overflow: 'hidden',
            }}
          >
            <SessionHelp onClose={handleClose} />
          </Paper>
        </Draggable>
      </div>
    </Modal>
  )
}
